import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CMYK } from "../lib/data";
import { Lightbox } from "./Lightbox";

const PRESS = [
  { src: "/images/collage_5.jpg", label: "FIG. 02 — THE PRESS ROOM", span: "md:col-span-7", h: "h-[280px] md:h-[460px]" },
  { src: "/images/collage_9.jpg", label: "FIG. 03 — 4-COLOUR UNIT", span: "md:col-span-5", h: "h-[280px] md:h-[460px]" },
  { src: "/images/collage_6.jpg", label: "FIG. 04 — PRECISION CUTTING", span: "md:col-span-5", h: "h-[280px] md:h-[400px]" },
  { src: "/images/collage_10.jpg", label: "FIG. 05 — FOLDING & FINISHING", span: "md:col-span-7", h: "h-[280px] md:h-[400px]" },
];

const WORK = [
  { src: "/images/people_4.jpg", label: "FIG. 06 — COFFEE TABLE BOOK" },
  { src: "/images/people_5.jpg", label: "FIG. 07 — PRODUCT CATALOG" },
  { src: "/images/people_7.jpg", label: "FIG. 08 — BROCHURE COVER" },
  { src: "/images/people_8.jpg", label: "FIG. 09 — OFF THE PRESS" },
];

const Frame = ({ img, i, onExpand }) => (
  <motion.figure
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay: (i % 2) * 0.12, ease: [0.16, 1, 0.3, 1] }}
    className={`${img.span || ""} border border-black bg-[#F4F4F0] cursor-zoom-in`}
    data-testid={`gallery-frame-${i}`}
    onClick={() => onExpand(img)}
  >
    <figcaption className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 border-b border-black flex justify-between items-center">
      <span>{img.label}</span>
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: CMYK[i % 4] }} />
    </figcaption>
    <div className={`overflow-hidden ${img.h || "h-[240px] md:h-[300px]"}`}>
      <img
        src={img.src}
        alt={img.label}
        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
      />
    </div>
  </motion.figure>
);

export const Gallery = () => {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="gallery" ref={ref} data-testid="gallery-section" className="border-b border-black relative overflow-hidden">
      <div className="hidden lg:block absolute -left-8 top-64 w-32 h-32 bg-[#EC008C] border border-black rotate-12" aria-hidden="true" />
      <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32 relative">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-black/60 mb-4">( Inside the press )</p>
        <h2 className="font-display font-bold uppercase tracking-tight leading-none text-4xl sm:text-6xl mb-16">
          On the <span className="text-[#FF3B30]">press</span>
        </h2>
        <motion.div style={{ y }} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {PRESS.map((img, i) => (
            <Frame key={i} img={img} i={i} onExpand={setExpanded} />
          ))}
        </motion.div>

        <div className="mt-24 md:mt-32">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-black/60 mb-4">( The Output )</p>
          <h2 className="font-display font-bold uppercase tracking-tight leading-none text-4xl sm:text-6xl mb-16">
            Fresh off the <span className="text-[#FF3B30]">sheet</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {WORK.map((img, i) => (
              <Frame key={i} img={img} i={i + 4} onExpand={setExpanded} />
            ))}
          </div>
        </div>
      </div>
      <Lightbox img={expanded} onClose={() => setExpanded(null)} />
    </section>
  );
};
